import { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { elementLabel, filterElements } from "../data/elements";

/**
 * Pick an element by typing.
 *
 * A dropdown was fine with a handful of entries. With 97 it is a scroll
 * through the periodic table to reach Tecnezio, and the list is ordered by
 * atomic number rather than alphabetically, so there is no letter to jump to.
 *
 * Typing matches the symbol and the name in both languages at once, so "tc",
 * "tecnezio" and "technetium" all find the same element whichever language
 * the interface is in. The list is always visible when the field has focus,
 * so it works as a browsable list too for somebody who does not know what
 * they are looking for.
 */
export default function ElementCombobox({ elements, value, onChange, testId }) {
  const { language } = useLanguage();
  const s = localStrings(language);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapper = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => elements.find((e) => e.id === value) || null,
    [elements, value]
  );

  const results = useMemo(
    () => filterElements(elements, query, language),
    [elements, query, language]
  );

  // Clicking anywhere else closes the list and drops whatever was half-typed,
  // so the field never shows a query that does not match what is selected.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => setHighlight(0), [query]);

  // Keep the highlighted row in view when arrowing past the visible window.
  useEffect(() => {
    const el = listRef.current?.children[highlight];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const choose = (el) => {
    onChange(el.id);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[highlight]) choose(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div ref={wrapper} className="relative">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          data-testid={testId}
          value={open ? query : (selected ? elementLabel(selected.name, language) : "")}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onKeyDown={onKeyDown}
          placeholder={s("decay.searchElement")}
          autoComplete="off"
          className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-600 focus:border-indigo-600"
        />
        {selected && !open && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label={s("date.clear")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-400">{s("decay.noElementMatch")}</p>
          ) : (
            <ul ref={listRef} className="max-h-64 overflow-y-auto py-1">
              {results.map((el, i) => (
                <li key={el.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => choose(el)}
                    data-testid={`element-option-${el.name}`}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                      i === highlight ? "bg-indigo-50 text-indigo-800" : "text-slate-700"
                    }`}
                  >
                    <span className="font-mono text-xs w-7 shrink-0 text-slate-400">{el.name}</span>
                    <span className="truncate">{elementLabel(el.name, language).split(" \u2014 ")[1] || el.name}</span>
                    <span className="ml-auto text-[11px] text-slate-400 shrink-0">
                      {el.isotopes?.length || 0}
                    </span>
                    {el.id === value && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
